import type {
  Timeline,
  CreateTimelineInput,
  UpdateTimelineInput,
  TimelineSection,
  CreateTimelineSectionInput,
  UpdateTimelineSectionInput,
  TimelineEntry,
  AddTimelineEntryInput,
  UpdateTimelineEntryInput,
  Checklist,
  CreateChecklistInput,
  UpdateChecklistInput,
  ChecklistSection,
  CreateChecklistSectionInput,
  UpdateChecklistSectionInput,
  ChecklistItem,
  CreateChecklistItemInput,
  UpdateChecklistItemInput,
  Kibuddim,
  CreateKibuddimInput,
  UpdateKibuddimInput,
  KibuddimSection,
  CreateKibuddimSectionInput,
  UpdateKibuddimSectionInput,
  KibuddimRole,
  CreateKibuddimRoleInput,
  UpdateKibuddimRoleInput,
  TaskQuery,
  TaskListResponse,
  BulkTaskInput,
  BulkTaskResult,
  TaskDateShiftPreview,
  TaskDateShiftResult,
  TimelineVendorTimes,
  AddTimelineVendorTimesInput,
  AddTimelineVendorTimesResult,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Timelines ──────────────────────────────────────────────────────────────

export async function getTimelines(eventId?: string): Promise<Timeline[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  const { data } = await apiClient.get<Timeline[]>('/event-ops/timelines', { params });
  return data;
}

export async function getTimeline(id: string): Promise<Timeline> {
  const { data } = await apiClient.get<Timeline>(`/event-ops/timelines/${id}`);
  return data;
}

export async function createTimeline(input: CreateTimelineInput): Promise<Timeline> {
  const { data } = await apiClient.post<Timeline>('/event-ops/timelines', input);
  return data;
}

export async function updateTimeline(id: string, updates: UpdateTimelineInput): Promise<Timeline> {
  const { data } = await apiClient.patch<Timeline>(`/event-ops/timelines/${id}`, updates);
  return data;
}

export async function deleteTimeline(id: string): Promise<void> {
  await apiClient.delete(`/event-ops/timelines/${id}`);
}

export async function cloneTimeline(
  id: string,
  body: { eventId?: string; title?: string },
): Promise<Timeline> {
  const { data } = await apiClient.post<Timeline>(`/event-ops/timelines/${id}/clone`, body);
  return data;
}

export async function addTimelineSection(
  timelineId: string,
  input: CreateTimelineSectionInput,
): Promise<TimelineSection> {
  const { data } = await apiClient.post<TimelineSection>(
    `/event-ops/timelines/${timelineId}/sections`,
    input,
  );
  return data;
}

export async function updateTimelineSection(
  sectionId: string,
  updates: UpdateTimelineSectionInput,
): Promise<TimelineSection> {
  const { data } = await apiClient.patch<TimelineSection>(
    `/event-ops/timelines/sections/${sectionId}`,
    updates,
  );
  return data;
}

export async function deleteTimelineSection(sectionId: string): Promise<void> {
  await apiClient.delete(`/event-ops/timelines/sections/${sectionId}`);
}

export async function addTimelineEntry(
  sectionId: string,
  input: AddTimelineEntryInput,
): Promise<TimelineEntry> {
  const { data } = await apiClient.post<TimelineEntry>(
    `/event-ops/timelines/sections/${sectionId}/entries`,
    input,
  );
  return data;
}

export async function updateTimelineEntry(
  entryId: string,
  updates: UpdateTimelineEntryInput,
): Promise<TimelineEntry> {
  const { data } = await apiClient.patch<TimelineEntry>(
    `/event-ops/timelines/entries/${entryId}`,
    updates,
  );
  return data;
}

export async function deleteTimelineEntry(entryId: string): Promise<void> {
  await apiClient.delete(`/event-ops/timelines/entries/${entryId}`);
}

/**
 * Every section of the timeline, once, in its new order, with the order the
 * page saw before the drop — refused (409) if that is no longer the stored one.
 */
export async function reorderTimelineSections(
  timelineId: string,
  sectionIds: string[],
  expected: string[],
): Promise<void> {
  await apiClient.put(`/event-ops/timelines/${timelineId}/section-order`, {
    sectionIds,
    expected,
  });
}

/** One section's new entry order — or the two an entry moved between — saved together. */
export interface TimelineEntryOrder {
  sectionId: string;
  entryIds: string[];
  /** The section's order as the page saw it before the drop. */
  expected: string[];
}

export async function reorderTimelineEntries(
  timelineId: string,
  sections: TimelineEntryOrder[],
): Promise<void> {
  await apiClient.put(`/event-ops/timelines/${timelineId}/entry-order`, { sections });
}

/** The bookings' arrival and load-in times, and which are on this timeline already. */
export async function getTimelineVendorTimes(timelineId: string): Promise<TimelineVendorTimes> {
  const { data } = await apiClient.get<TimelineVendorTimes>(
    `/event-ops/timelines/${timelineId}/vendor-times`,
  );
  return data;
}

/** Puts the chosen bookings' times on the timeline; the times are read from the bookings. */
export async function addTimelineVendorTimes(
  timelineId: string,
  input: AddTimelineVendorTimesInput,
): Promise<AddTimelineVendorTimesResult> {
  const { data } = await apiClient.post<AddTimelineVendorTimesResult>(
    `/event-ops/timelines/${timelineId}/vendor-times`,
    input,
  );
  return data;
}

// ─── Checklists ─────────────────────────────────────────────────────────────

export async function getChecklists(
  eventId?: string,
  options: { templates?: boolean } = {},
): Promise<Checklist[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  if (options.templates) params.templates = 'true';
  const { data } = await apiClient.get<Checklist[]>('/event-ops/checklists', { params });
  return data;
}

export async function getChecklist(id: string): Promise<Checklist> {
  const { data } = await apiClient.get<Checklist>(`/event-ops/checklists/${id}`);
  return data;
}

export async function createChecklist(input: CreateChecklistInput): Promise<Checklist> {
  const { data } = await apiClient.post<Checklist>('/event-ops/checklists', input);
  return data;
}

export async function updateChecklist(
  id: string,
  updates: UpdateChecklistInput,
): Promise<Checklist> {
  const { data } = await apiClient.patch<Checklist>(`/event-ops/checklists/${id}`, updates);
  return data;
}

export async function deleteChecklist(id: string): Promise<void> {
  await apiClient.delete(`/event-ops/checklists/${id}`);
}

export async function cloneChecklist(
  id: string,
  body: { eventId?: string; title?: string },
): Promise<Checklist> {
  const { data } = await apiClient.post<Checklist>(`/event-ops/checklists/${id}/clone`, body);
  return data;
}

export async function addChecklistSection(
  checklistId: string,
  input: CreateChecklistSectionInput,
): Promise<ChecklistSection> {
  const { data } = await apiClient.post<ChecklistSection>(
    `/event-ops/checklists/${checklistId}/sections`,
    input,
  );
  return data;
}

export async function updateChecklistSection(
  sectionId: string,
  updates: UpdateChecklistSectionInput,
): Promise<ChecklistSection> {
  const { data } = await apiClient.patch<ChecklistSection>(
    `/event-ops/checklists/sections/${sectionId}`,
    updates,
  );
  return data;
}

export async function deleteChecklistSection(sectionId: string): Promise<void> {
  await apiClient.delete(`/event-ops/checklists/sections/${sectionId}`);
}

export async function addChecklistItem(
  sectionId: string,
  input: CreateChecklistItemInput,
): Promise<ChecklistItem> {
  const { data } = await apiClient.post<ChecklistItem>(
    `/event-ops/checklists/sections/${sectionId}/items`,
    input,
  );
  return data;
}

export async function updateChecklistItem(
  itemId: string,
  updates: UpdateChecklistItemInput,
): Promise<ChecklistItem> {
  const { data } = await apiClient.patch<ChecklistItem>(
    `/event-ops/checklists/items/${itemId}`,
    updates,
  );
  return data;
}

export async function deleteChecklistItem(itemId: string): Promise<void> {
  await apiClient.delete(`/event-ops/checklists/items/${itemId}`);
}

export async function toggleChecklistItem(itemId: string, isDone: boolean): Promise<ChecklistItem> {
  const { data } = await apiClient.post<ChecklistItem>(
    `/event-ops/checklists/items/${itemId}/toggle`,
    { isDone },
  );
  return data;
}

export interface CalendarTaskItem {
  id: string;
  title: string;
  isDone: boolean;
  status: string;
  dueDate: string | null;
  assignedToUserId: string | null;
  assignedTo: { id: string; name: string } | null;
  section: {
    checklist: {
      id: string;
      title: string | null;
      eventId: string | null;
    };
  };
}

export interface ChecklistItemsForCalendarResult {
  items: CalendarTaskItem[];
  overdueItems: CalendarTaskItem[];
}

export async function getChecklistItemsForCalendar(params: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<ChecklistItemsForCalendarResult> {
  const { data } = await apiClient.get<ChecklistItemsForCalendarResult>(
    '/event-ops/checklist-items',
    { params },
  );
  return data;
}

// ─── Kibuddim ───────────────────────────────────────────────────────────────

export async function getKibuddimList(eventId?: string): Promise<Kibuddim[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  const { data } = await apiClient.get<Kibuddim[]>('/event-ops/kibuddim', { params });
  return data;
}

export async function getKibuddimSingle(id: string): Promise<Kibuddim> {
  const { data } = await apiClient.get<Kibuddim>(`/event-ops/kibuddim/${id}`);
  return data;
}

export async function createKibuddim(input: CreateKibuddimInput): Promise<Kibuddim> {
  const { data } = await apiClient.post<Kibuddim>('/event-ops/kibuddim', input);
  return data;
}

export async function updateKibuddim(id: string, updates: UpdateKibuddimInput): Promise<Kibuddim> {
  const { data } = await apiClient.patch<Kibuddim>(`/event-ops/kibuddim/${id}`, updates);
  return data;
}

export async function deleteKibuddim(id: string): Promise<void> {
  await apiClient.delete(`/event-ops/kibuddim/${id}`);
}

export async function cloneKibuddim(
  id: string,
  body: { eventId?: string; title?: string },
): Promise<Kibuddim> {
  const { data } = await apiClient.post<Kibuddim>(`/event-ops/kibuddim/${id}/clone`, body);
  return data;
}

export async function addKibuddimSection(
  kibuddimId: string,
  input: CreateKibuddimSectionInput,
): Promise<KibuddimSection> {
  const { data } = await apiClient.post<KibuddimSection>(
    `/event-ops/kibuddim/${kibuddimId}/sections`,
    input,
  );
  return data;
}

export async function updateKibuddimSection(
  sectionId: string,
  updates: UpdateKibuddimSectionInput,
): Promise<KibuddimSection> {
  const { data } = await apiClient.patch<KibuddimSection>(
    `/event-ops/kibuddim/sections/${sectionId}`,
    updates,
  );
  return data;
}

export async function deleteKibuddimSection(sectionId: string): Promise<void> {
  await apiClient.delete(`/event-ops/kibuddim/sections/${sectionId}`);
}

export async function addKibuddimRole(
  sectionId: string,
  input: CreateKibuddimRoleInput,
): Promise<KibuddimRole> {
  const { data } = await apiClient.post<KibuddimRole>(
    `/event-ops/kibuddim/sections/${sectionId}/roles`,
    input,
  );
  return data;
}

export async function updateKibuddimRole(
  roleId: string,
  updates: UpdateKibuddimRoleInput,
): Promise<KibuddimRole> {
  const { data } = await apiClient.patch<KibuddimRole>(
    `/event-ops/kibuddim/roles/${roleId}`,
    updates,
  );
  return data;
}

export async function deleteKibuddimRole(roleId: string): Promise<void> {
  await apiClient.delete(`/event-ops/kibuddim/roles/${roleId}`);
}

// ─── Tasks (items across lists) ─────────────────────────────────────────────

export async function listTasks(query: TaskQuery): Promise<TaskListResponse> {
  const params: Record<string, string | number> = {};
  if (query.eventId) params.eventId = query.eventId;
  if (query.assignee?.length) params.assignee = query.assignee.join(',');
  if (query.status) params.status = query.status;
  if (query.due?.length) params.due = query.due.join(',');
  if (query.q?.trim()) params.q = query.q.trim();
  if (query.date) params.date = query.date;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset) params.offset = query.offset;
  const { data } = await apiClient.get<TaskListResponse>('/event-ops/tasks', { params });
  return data;
}

export async function bulkUpdateTasks(input: BulkTaskInput): Promise<BulkTaskResult> {
  const { data } = await apiClient.post<BulkTaskResult>('/event-ops/tasks/bulk', input);
  return data;
}

/** Which of an event's tasks would follow its date from `from` to the one it has now. */
export async function previewTaskDateShift(
  eventId: string,
  from: string,
): Promise<TaskDateShiftPreview> {
  const { data } = await apiClient.get<TaskDateShiftPreview>(
    `/event-ops/events/${eventId}/task-date-shift`,
    { params: { from } },
  );
  return data;
}

/** Moves the chosen tasks with the event. */
export async function applyTaskDateShift(
  eventId: string,
  from: string,
  to: string,
  itemIds: string[],
): Promise<TaskDateShiftResult> {
  const { data } = await apiClient.post<TaskDateShiftResult>(
    `/event-ops/events/${eventId}/task-date-shift`,
    { from, to, itemIds },
  );
  return data;
}

export async function reorderChecklistSections(
  checklistId: string,
  sectionIds: string[],
): Promise<void> {
  await apiClient.put(`/event-ops/checklists/${checklistId}/section-order`, { sectionIds });
}

export async function reorderChecklistItems(sectionId: string, itemIds: string[]): Promise<void> {
  await apiClient.put(`/event-ops/checklists/sections/${sectionId}/item-order`, { itemIds });
}
